import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Input,
  Spinner,
  Typography,
} from "@material-tailwind/react";
import toast from "react-hot-toast";
import AlertMessage from "../../common/actions/AlertMessage";
import InputText from "../../common/forms/InputText";
import Loader from "../../common/loader/Loader";
import SectionTitle from "../../common/shared/SectionTitle";
import {
  applyNfcCard,
  getUserNfcCards,
  uploadNfcCardPhoto,
} from "../../redux/actions/nfc_card_action";
import NfcAccountCard from "./components/NfcAccountCard";
import NfcCardPay from "./components/NfcCardPay";

const link = [{ name: "NFC Identification Card" }];
const amount = 150;

const statusLabel = {
  processing: "Processing",
  ready: "Ready",
  completed: "Completed",
};

function NfcCard() {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({ ghana_card_number: "" });
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const [autoPayCardId, setAutoPayCardId] = useState(null);

  const dispatch = useDispatch();
  const location = useLocation();
  const routePrefix = location.pathname.startsWith("/mobile")
    ? "/mobile"
    : "/home";
  const { data, card, nfcCardError } = useSelector((state) => state.nfcCard);
  const userData = useSelector((state) => state.auth?.userData);
  const storedUser = JSON.parse(localStorage.getItem("profile") || "{}")?.user;
  const user = userData || storedUser;
  const isInGoodStanding = user?.standing === true;

  const unpaidApplication = useMemo(
    () => data?.find((item) => item.paid !== 1),
    [data],
  );

  const applications = useMemo(() => {
    const latestCard = currentCard || card;
    const existingApplications = data || [];

    if (!latestCard?.id) {
      return existingApplications;
    }

    const hasLatestCard = existingApplications.some(
      (item) => item.id === latestCard.id,
    );

    if (!hasLatestCard) {
      return [latestCard, ...existingApplications];
    }

    return existingApplications.map((item) =>
      item.id === latestCard.id ? latestCard : item,
    );
  }, [card, currentCard, data]);

  const canApply = applications.length === 0;
  const showApplicationForm =
    isInGoodStanding && (canApply || Boolean(editingApplication));

  const updateFormValue = ({ updateType, value }) => {
    setFormData({ ...formData, [updateType]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.ghana_card_number) {
      toast.error("Please enter your Ghana Card number.");
      return;
    }

    if (!passportPhoto && !editingApplication?.passport_photo) {
      toast.error("Please upload your passport-sized picture.");
      return;
    }

    setSubmitLoading(true);

    dispatch(applyNfcCard(formData))
      .then((response) => {
        const responseCard = response?.data?.card;
        if (responseCard) {
          setCurrentCard(responseCard);

          if (!passportPhoto) {
            setEditingApplication(null);
            return response;
          }

          const fileData = new FormData();
          fileData.append("file", passportPhoto);
          return dispatch(uploadNfcCardPhoto(responseCard.id, fileData)).then(
            (uploadResponse) => {
              const uploadedCard = uploadResponse?.data?.card;
              if (uploadedCard) {
                setCurrentCard(uploadedCard);
                if (!editingApplication) {
                  setAutoPayCardId(uploadedCard.id);
                }
              }
              setEditingApplication(null);
              setPassportPhoto(null);
              return uploadResponse;
            },
          );
        }

        return response;
      })
      .finally(() => setSubmitLoading(false));
  };

  const handleEdit = (item) => {
    setEditingApplication(item);
    setPassportPhoto(null);
    setFormData({
      ghana_card_number: item.ghana_card_number || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingApplication(null);
    setPassportPhoto(null);
    setFormData({ ghana_card_number: "" });
  };

  useEffect(() => {
    dispatch(getUserNfcCards()).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (unpaidApplication?.ghana_card_number) {
      setFormData({
        ghana_card_number: unpaidApplication.ghana_card_number,
      });
    }
  }, [unpaidApplication]);

  return (
    <>
      <SectionTitle
        title="NFC Identification Card"
        description=""
        children={link}
        link={routePrefix}
      />

      <div className="my-8 max-w-[1200px] m-auto px-4">
        {nfcCardError && <AlertMessage type="error" message={nfcCardError} />}

        {loading ? (
          <Loader />
        ) : (
          <>
            {!isInGoodStanding && (
              <AlertMessage
                type="error"
                message="Please be in good financial standing to apply for an NFC card."
              />
            )}

            {showApplicationForm && (
              <Card className="mb-8">
                <form onSubmit={handleSubmit}>
                  <CardBody>
                    <Typography variant="h5" color="black" className="mb-6">
                      {editingApplication
                        ? "Edit NFC Identification Card Application"
                        : "Apply for NFC Identification Card"}
                    </Typography>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <InputText
                          key={
                            formData.ghana_card_number || "ghana-card-number"
                          }
                          labelTitle="Ghana Card Number"
                          defaultValue={formData.ghana_card_number}
                          updateType="ghana_card_number"
                          updateFormValue={updateFormValue}
                          required
                        />
                        <Typography
                          variant="small"
                          color="gray"
                          className="mt-2 flex items-center gap-1 font-normal"
                        >
                          Please provide Ghana Card number in this format:
                          GHA-123456789-0
                        </Typography>
                      </div>

                      <div>
                        <Input
                          color="black"
                          type="file"
                          label="Profile Image"
                          variant="static"
                          accept="image/*"
                          onChange={(event) =>
                            setPassportPhoto(event.target.files?.[0] || null)
                          }
                          disabled={submitLoading}
                          required={!editingApplication?.passport_photo}
                        />
                        <Typography
                          variant="small"
                          color="black"
                          className="mt-2 flex items-center gap-1 font-normal"
                        >
                          {editingApplication?.passport_photo
                            ? "Upload a new profile image only if you want to replace the current one."
                            : "Upload a clear profile image before making payment."}
                        </Typography>
                      </div>
                    </div>
                  </CardBody>
                  <CardFooter className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      color="blue-gray"
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <Spinner className="h-4 w-4" />
                      ) : (
                        "Submit Details"
                      )}
                    </Button>

                    {editingApplication && (
                      <Button
                        type="button"
                        variant="outlined"
                        color="blue-gray"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </Button>
                    )}
                  </CardFooter>
                </form>
              </Card>
            )}

            {!applications.length ? (
              null
            ) : (
              applications.map((item) => (
                <Card key={item.id} className="mb-6">
                  <CardBody>
                    <Typography variant="h5" color="black">
                      NFC Identification Card Application
                    </Typography>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 my-4">
                      <div>
                        <span>Ghana Card Number</span>
                        <Typography variant="h6" color="black">
                          {item.ghana_card_number}
                        </Typography>
                      </div>
                      <div>
                        <span>Amount</span>
                        <Typography variant="h6" color="black">
                          GH¢ {item.amount || amount}
                        </Typography>
                      </div>
                      <div>
                        <span>Payment</span>
                        <Typography variant="h6" color="black">
                          {item.paid === 1 ? "Paid" : "Not Paid"}
                        </Typography>
                      </div>
                      <div>
                        <span>Status</span>
                        <Typography variant="h6" color="black">
                          {item.paid === 1
                            ? statusLabel[item.status] || item.status
                            : "Please make payment"}
                        </Typography>
                      </div>
                    </div>

                    {item.status === "completed" && (
                      <NfcAccountCard compact />
                    )}
                  </CardBody>

                  {item.paid !== 1 && isInGoodStanding && (
                    <CardFooter className="flex flex-col sm:flex-row gap-3">
                      <Button
                        type="button"
                        color="blue-gray"
                        variant="outlined"
                        onClick={() => handleEdit(item)}
                      >
                        Edit Details
                      </Button>
                      {item.passport_photo && (
                        <NfcCardPay
                          id={item.id}
                          amount={item.amount || amount}
                          autoOpen={autoPayCardId === item.id}
                          onAutoOpen={() => setAutoPayCardId(null)}
                        />
                      )}
                    </CardFooter>
                  )}
                </Card>
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}

export default NfcCard;
